import { courtBranchSchema } from "@/features/court-detail/types/court-branches";
import { z } from "zod";

export const loginFormSchema = z.object({
	email: z.string().min(5, { message: "Required" }),
	password: z.string().min(6, { message: "Required" }),
	remember: z.boolean(),
});

export const roleCreateSchema = z.object({
	role_id: z.string(),
	name: z.string().nullable(),
});

export const userPinSchema = z.object({
	pin_code: z
		.string()
		.max(6, {
			message: "Pin code must be exactly 6 characters long.",
		})
		.min(6, {
			message: "Pin code must be exactly 6 characters long.",
		}),
});

export const roleSchema = z.object({
	id: z.string().nullable(),
	name: z.string(),
	description: z.string().optional(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
});

export const userSchema = z.object({
	id: z.string(),
	status: z.string(),
	email: z.string(),
	avatar_thumbnail: z.string().optional(),
	avatar_url: z.string().optional(),
	first_name: z.string(),
	middle_name: z.string().optional(),
	last_name: z.string(),
	initials: z.string().optional(),
	suffix: z.string().optional(),
	full_name: z.string().optional(),
	roles: z.array(roleSchema),
	is_occ: z.boolean().default(false),
	is_bcc: z.boolean().default(false),
	is_counsel: z.boolean().default(false),
	sex: z.string().optional(),
	birthdate: z.string().optional(),
	birthplace: z.string().optional(),
	region_code: z.string().optional(),
	province_code: z.string().optional(),
	city_code: z.string().optional(),
	barangay: z.string().optional(),
	address_1: z.string().optional(),
	address_2: z.string().optional(),
	zipcode: z.union([z.string(), z.number()]).nullable().optional(),
	law_firm: z.string().optional(),
	law_firm_email: z.string().optional(),
	address: z.string().optional(),
	contact: z.string().optional(),
	last_login_at: z.string().optional(),
	disabled_at: z.string().optional(),
	created_at: z.string().optional(),
	updated_at: z.string().optional(),
	pin_code_updated_at: z.string().optional(),
	branches: z.array(courtBranchSchema).optional(),
});

export const userEmailSchema = z.object({
	id: z.string(),
	email: z.string(),
});

export const profileFormSchema = z.object({
	id: z.string().optional(),
	avatar: z.string().optional(),
	full_name: z.string().optional(),
	first_name: z
		.string()
		.min(2, {
			message: "Name must be at least 2 characters.",
		})
		.max(50, {
			message: "Name must not be longer than 30 characters.",
		}),
	middle_name: z.string().optional(),
	last_name: z
		.string()
		.min(2, {
			message: "Name must be at least 2 characters.",
		})
		.max(50, {
			message: "Name must not be longer than 30 characters.",
		}),
	suffix: z.string().optional(),
	initials: z.string().optional(),
	email: z
		.string({
			required_error: "Email is required.",
		})
		.email(),
});

export const passwordFormSchema = z
	.object({
		id: z.string().optional(),
		password: z.string().min(6, {
			message: "Password should be least 6 characters long.",
		}),
		password_confirmation: z.string(),
		current_password: z.string().min(1, {
			message: "Current password is required.",
		}),
	})
	.refine((data) => data.password === data.password_confirmation, {
		message: "Passwords must match.",
		path: ["password_confirmation"],
	});

export const userFormSchema = z
	.object({
		id: z.string().optional(),
		avatar: z.string().optional(),
		avatar_thumbnail: z.string().optional(),
		roles: z.array(roleCreateSchema).optional(),
		first_name: z
			.string()
			.min(2, {
				message: "Name must be at least 2 characters.",
			})
			.max(50, {
				message: "Name must not be longer than 30 characters.",
			}),
		middle_name: z
			.string()
			.max(50, {
				message: "Name must not be longer than 30 characters.",
			})
			.optional(),
		last_name: z
			.string()
			.min(2, {
				message: "Name must be at least 2 characters.",
			})
			.max(50, {
				message: "Name must not be longer than 30 characters.",
			}),
		initials: z.string().optional(),
		suffix: z.string().optional(),
		contact_number: z
			.string()
			.regex(/^\+639\d{9}$/, {
				message: "Contact number must be a valid Philippine number (e.g., +63917XXXXXXXX)",
			})
			.optional()
			.or(z.literal("")),
		sex: z.string().optional(),
		birthdate: z.string().optional(),
		preferred_gender: z.string().optional(),
		preferred_middle_initials: z.string().optional(),
		email: z
			.string({
				required_error: "Email is required.",
			})
			.email(),
		region_code: z.string(),
		province_code: z.string(),
		zipcode: z.union([z.string(), z.number()]).nullable().optional(),
		city_code: z.string(),
		address_1: z.string(),
		address_2: z.string(),
		court_branch_id: z.string().nullable().optional(),
		court_branch: z.string().nullable().optional(),
		password: z.string().nullable().optional(),
		password_confirmation: z.string().nullable().optional(),
		barangay: z.string(),
		post_as_judge: z.any().optional(),
		post_as_multiple_role: z.any().optional(),
	})
	.refine((data) => data.password === data.password_confirmation, {
		message: "Passwords must match.",
		path: ["password_confirmation"],
	});

export const userCreateFormSchema = z
	.object({
		id: z.string().nullable().optional(),
		avatar: z.string().optional(),
		avatar_thumbnail: z.string().optional(),
		roles: z.array(roleCreateSchema).optional(),
		first_name: z
			.string()
			.min(2, {
				message: "Name must be at least 2 characters.",
			})
			.max(50, {
				message: "Name must not be longer than 30 characters.",
			}),
		middle_name: z
			.string()
			.max(50, {
				message: "Name must not be longer than 30 characters.",
			})
			.optional(),
		last_name: z
			.string()
			.min(2, {
				message: "Name must be at least 2 characters.",
			})
			.max(50, {
				message: "Name must not be longer than 30 characters.",
			}),
		initials: z.string().optional(),
		suffix: z.string().optional(),
		contact_number: z
			.string()
			.regex(/^\+639\d{9}$/, {
				message: "Contact number must be a valid Philippine number (e.g., +63917XXXXXXXX)",
			})
			.optional()
			.or(z.literal("")),
		sex: z.string().optional(),
		birthdate: z.string().optional(),
		preferred_gender: z.string().optional(),
		preferred_middle_initials: z.string().optional(),
		email: z
			.string({
				required_error: "Email is required.",
			})
			.email(),
		region_code: z.string().nullable().optional(),
		province_code: z.string().nullable().optional(),
		zipcode: z.union([z.string(), z.number()]).nullable().optional(),
		city_code: z.string().nullable().optional(),
		address_1: z.string().nullable().optional(),
		address_2: z.string().nullable().optional(),
		court_branch_id: z.string().nullable().optional(),
		court_branch: z.any().nullable().optional(),
		password: z.string().nullable().optional(),
		password_confirmation: z.string().nullable().optional(),
		barangay: z.string().nullable().optional(),
		post_as_judge: z.any().optional(),
		post_as_multiple_role: z.any().optional(),
	})
	.refine((data) => data.password === data.password_confirmation, {
		message: "Passwords must match.",
		path: ["password_confirmation"],
	});

export const userPostAsFormSchema = z.object({
	id: z.string().optional(),
	allow_post_as: z.any().optional(),
});

export const isLeadFormSchema = z.object({
	id: z.string().optional(),
	is_lead_counsel: z.any().optional(),
});

export const userAssignBranchFormSchema = z.object({
	user_id: z.string().optional(),
	court_branch: z.any().nullable().optional(),
	court_branch_id: z.string().optional(),
});

export type UserEmail = z.infer<typeof userEmailSchema>;
export type User = z.infer<typeof userSchema>;
export type UserPinSchema = z.infer<typeof userPinSchema>;
export type Roles = z.infer<typeof roleSchema>;
export type UserForm = z.infer<typeof userFormSchema>;
export type ProfileFormSchema = z.infer<typeof profileFormSchema>;
export type PasswordFormSchema = z.infer<typeof passwordFormSchema>;
export type LoginFormSchema = z.infer<typeof loginFormSchema>;
export type UserFormSchema = z.infer<typeof userFormSchema>;
export type UserPostAsFormSchema = z.infer<typeof userPostAsFormSchema>;
export type UserCreateFormSchema = z.infer<typeof userCreateFormSchema>;
export type UserAssignBranchFormSchema = z.infer<typeof userAssignBranchFormSchema>;
export type isLeadFormSchema = z.infer<typeof isLeadFormSchema>;
